import { Link } from "wouter";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@workspace/ui-web/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui-web/components/ui/card";
import { Gift, Heart, Dog, Check, ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/seo";

const wishlistItems = [
  "Puppy and adult dog food",
  "Collars, leashes, and training harnesses",
  "Crates and bedding",
  "Training treats and enrichment toys",
  "Grooming and hygiene supplies",
  "Veterinary and vaccination support",
];

export default function WaysToGive() {
  useSeo({
    title: "Ways to Give: Donate, Sponsor a Puppy | Greater Hope Service Dogs",
    description:
      "Support service dogs for people in recovery: donate supplies from our wishlist, sponsor a puppy's training, or donate a dog to our program.",
    path: "/ways-to-give",
  });
  const zeffyUrl = import.meta.env.VITE_ZEFFY_DONATION_URL as
    | string
    | undefined;
  const wishlistUrl = import.meta.env.VITE_WISHLIST_URL as string | undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Header */}
        <section className="bg-primary text-primary-foreground py-20">
          <div className="container px-4 md:px-8 max-w-4xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Ways to Give
            </h1>
            <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
              It costs over $25,000 to breed, raise, and train a single service
              dog. Every contribution — supplies, sponsorship, or a dog itself —
              moves a person in recovery closer to their lifeline.
            </p>
          </div>
        </section>

        {/* Three giving options */}
        <section className="py-20 bg-background">
          <div className="container px-4 md:px-8 max-w-6xl mx-auto">
            <div className="grid gap-8 lg:grid-cols-3">
              {/* Supplies Wishlist */}
              <Card className="flex flex-col">
                <CardHeader className="space-y-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Gift className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-2xl">Supplies Wishlist</CardTitle>
                  <p className="text-muted-foreground">
                    Donate the everyday essentials our puppies need as they grow
                    into working service dogs.
                  </p>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <ul className="space-y-3 mb-6">
                    {wishlistItems.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                      >
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    {wishlistUrl ? (
                      <Button asChild className="w-full">
                        <a
                          href={wishlistUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Our Wishlist
                        </a>
                      </Button>
                    ) : (
                      <Button asChild variant="outline" className="w-full">
                        <Link href="/contact">
                          Contact Us to Donate Supplies
                        </Link>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Sponsor a Puppy */}
              <Card className="flex flex-col border-primary/40 shadow-md">
                <CardHeader className="space-y-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Heart className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-2xl">Sponsor a Puppy</CardTitle>
                  <p className="text-muted-foreground">
                    Fund the care, training, and veterinary needs of a puppy on
                    its journey to becoming a service dog. Your gift directly
                    supports a future placement.
                  </p>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <div className="rounded-xl bg-accent/40 p-4 text-sm text-muted-foreground mb-6">
                    Sponsorships are processed securely through Zeffy at the
                    giving level of your choice. One hundred percent of your
                    donation supports our dogs.
                  </div>
                  <div className="mt-auto">
                    {zeffyUrl ? (
                      <Button asChild className="w-full">
                        <a
                          href={zeffyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Sponsor a Puppy via Zeffy
                        </a>
                      </Button>
                    ) : (
                      <Button asChild variant="outline" className="w-full">
                        <Link href="/contact">Contact Us to Sponsor</Link>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Donate a Dog */}
              <Card className="flex flex-col">
                <CardHeader className="space-y-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Dog className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-2xl">Donate a Dog</CardTitle>
                  <p className="text-muted-foreground">
                    Have a dog with the right temperament for service work, or a
                    breeding prospect you would like to donate to our program?
                    We would love to talk with you.
                  </p>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <div className="rounded-xl bg-accent/40 p-4 text-sm text-muted-foreground mb-6">
                    Every donated dog is carefully evaluated to ensure a
                    healthy, successful path into our training program. Reach
                    out and our team will guide you through the process.
                  </div>
                  <div className="mt-auto">
                    <Button asChild className="w-full gap-2">
                      <Link href="/contact">
                        Start the Conversation{" "}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Reference to existing monetary giving */}
            <div className="mt-16 text-center max-w-2xl mx-auto space-y-4">
              <h2 className="text-2xl font-bold text-foreground">
                Prefer to make a general donation?
              </h2>
              <p className="text-muted-foreground">
                You can also support our mission with a one-time or recurring
                monetary gift that funds every part of breeding, raising, and
                training our service dogs.
              </p>
              {zeffyUrl ? (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                >
                  <a href={zeffyUrl} target="_blank" rel="noopener noreferrer">
                    Donate via Zeffy
                  </a>
                </Button>
              ) : (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                >
                  <Link href="/contact">Contact Us to Give</Link>
                </Button>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
